import { ReactNode, useEffect, useMemo } from 'react';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { IS_DARK_MODE_LOCAL_STOREAGE_KEY, IS_DARK_MODE_LOCAL_STOREAGE_OVERRIDE_KEY, LightMode } from '../../constants';
import { Color } from './ThemeConstants';
import { getColorName } from './utils';
import { ThemeContext } from './ThemeContext';

export const ThemeProvider = (props: Props) => {
  const { children, color, lightMode } = props;
  const themeColor = color === undefined ? Color.blue : color;
  const [isDarkMode, setDarkMode] = useLocalStorageState<boolean>(IS_DARK_MODE_LOCAL_STOREAGE_KEY, false);
  const isLightModeOverridden = lightMode != null && lightMode !== LightMode.any;
  const isActuallyDarkMode = isLightModeOverridden ? lightMode === LightMode.dark : isDarkMode;

  useEffect(() => {
    if (lightMode) {
      localStorage.setItem(IS_DARK_MODE_LOCAL_STOREAGE_OVERRIDE_KEY, lightMode === LightMode.dark ? 'true' : 'false');
    } else {
      localStorage.removeItem(IS_DARK_MODE_LOCAL_STOREAGE_OVERRIDE_KEY);
    }
  }, [lightMode]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDarkMode(!isDarkMode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDarkMode, setDarkMode]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      color: themeColor,
      colorName: getColorName(themeColor),
      isDarkMode: isActuallyDarkMode,
      toggleDarkMode: isLightModeOverridden ? () => null : () => setDarkMode(!isDarkMode),
    }),
    [isLightModeOverridden, isActuallyDarkMode, isDarkMode, setDarkMode]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

type Props = {
  children: ReactNode;
  color?: Color;
  lightMode?: LightMode;
  minimumDelay?: number;
};
